/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { api } from '@/lib/api'
import { computeTimeRange } from '@/lib/time'
import type {
  QuotaDataItem,
  LogStatistics,
  TrendDataPoint,
  ModelDistributionItem,
} from './types'

// ============================================================================
// Overview Stats
// ============================================================================

export async function getOverviewStats() {
  const now = Math.floor(Date.now() / 1000)

  // Get quota data for total calls (last 30 days)
  const quotaRes = await api.get<{ success: boolean; data: QuotaDataItem[] }>(
    '/api/data',
    {
      params: {
        start_timestamp: now - 30 * 24 * 3600,
        end_timestamp: now,
        default_time: 'day',
      },
    }
  )

  // Get log stats for RPM
  const logRes = await api.get<{ success: boolean; data?: LogStatistics }>(
    '/api/log/stat',
    { params: { type: 2 } }
  )

  // Get user list for active users count
  const usersRes = await api.get<{
    success: boolean
    data?: { items: Array<{ status: number }>; total: number }
  }>('/api/user/', { params: { p: 1, page_size: 1 } })

  // Get channel list for online channels
  const channelsRes = await api.get<{
    success: boolean
    data?: { items: Array<{ status: number }>; total: number }
  }>('/api/channel/', { params: { p: 1, page_size: 1 } })

  const quotaData = quotaRes.data.data ?? []
  const totalCalls = quotaData.reduce(
    (sum, item) => sum + (Number(item.count) || 0),
    0
  )
  const totalQuota = quotaData.reduce(
    (sum, item) => sum + (Number(item.quota) || 0),
    0
  )

  const activeUsers = usersRes.data.data?.total ?? 0
  const totalChannels = channelsRes.data.data?.total ?? 0
  const onlineChannels =
    channelsRes.data.data?.items.filter((c) => c.status === 1).length ??
    totalChannels

  return {
    totalCalls,
    activeUsers,
    dataStorage: totalQuota,
    uptime: totalChannels > 0 ? (onlineChannels / totalChannels) * 100 : 100,
    rpm: logRes.data.data?.rpm ?? 0,
  }
}

// ============================================================================
// Trend Data
// ============================================================================

export async function getTrendData(
  range: '24h' | '7d' | '30d'
): Promise<TrendDataPoint[]> {
  const days = range === '24h' ? 1 : range === '7d' ? 7 : 30
  const { start_timestamp, end_timestamp } = computeTimeRange(days)

  const res = await api.get<{ success: boolean; data: QuotaDataItem[] }>(
    '/api/data',
    {
      params: {
        start_timestamp,
        end_timestamp,
        default_time: range === '24h' ? 'hour' : 'day',
      },
    }
  )

  const data = res.data.data ?? []

  // Group by time bucket
  const bucketMap = new Map<string, { timestamp: number; value: number }>()

  for (const item of data) {
    const ts = Number(item.created_at) || 0
    if (!ts) continue

    const date = new Date(ts * 1000)
    let key: string

    if (range === '24h') {
      date.setMinutes(0, 0, 0)
      key = date.toISOString()
    } else {
      date.setHours(0, 0, 0, 0)
      key = date.toISOString()
    }

    const existing = bucketMap.get(key)
    if (existing) {
      existing.value += Number(item.count) || 0
    } else {
      bucketMap.set(key, { timestamp: Math.floor(date.getTime() / 1000), value: Number(item.count) || 0 })
    }
  }

  const sorted = Array.from(bucketMap.values()).sort(
    (a, b) => a.timestamp - b.timestamp
  )

  return sorted.map((item) => ({
    timestamp: item.timestamp,
    label: formatTrendLabel(item.timestamp, range),
    value: item.value,
  }))
}

function formatTrendLabel(timestamp: number, range: '24h' | '7d' | '30d'): string {
  const date = new Date(timestamp * 1000)
  if (range === '24h') {
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' })
}

// ============================================================================
// Model Distribution
// ============================================================================

export async function getModelDistribution(): Promise<ModelDistributionItem[]> {
  const { start_timestamp, end_timestamp } = computeTimeRange(7)

  const res = await api.get<{ success: boolean; data: QuotaDataItem[] }>(
    '/api/data',
    {
      params: {
        start_timestamp,
        end_timestamp,
        default_time: 'day',
      },
    }
  )

  const data = res.data.data ?? []

  // Group by model name
  const modelMap = new Map<string, { value: number; count: number }>()

  for (const item of data) {
    const model = item.model_name || 'Unknown'
    const existing = modelMap.get(model)
    if (existing) {
      existing.value += Number(item.quota) || 0
      existing.count += Number(item.count) || 0
    } else {
      modelMap.set(model, {
        value: Number(item.quota) || 0,
        count: Number(item.count) || 0,
      })
    }
  }

  const sorted = Array.from(modelMap.entries())
    .map(([name, stats]) => ({
      name,
      value: stats.value,
      count: stats.count,
    }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 8)

  return sorted
}
